import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { ConfiguratorModes } from "utils/enums";
import { DashboardTabModel } from "api/model/dashboard/DashboardTab";
import React, { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { T } from "services/lang/LanguageService";
import { DashboardLayoutType, IDashboardComponent } from "api/model/dashboard/DashboardComponent";
import useDashboardStore from "store/dashboard-store";
import useTabId from "hooks/dashboard/useTabId";
import { Dashboard } from "api/model/dashboard/Dashboard";
import ComponentThemeEditor from "./ComponentThemeEditor";
import ComponentTypeEditor from "./ComponentTypeEditor";
import ComponentEditor from "./ComponentEditor";
import { defaultComponent } from "./GroupComponentConfiguratorNew";

interface GroupHeaderComponentConfiguratorProps {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  component?: IDashboardComponent;
  mode: ConfiguratorModes;
  groupId: string;
}

const GroupHeaderComponentConfigurator = ({
  isVisible,
  setIsVisible,
  mode,
  component,
  groupId,
}: GroupHeaderComponentConfiguratorProps) => {
  const dashboard = useDashboardStore(state => state.dashboard);
  const setDashboard = useDashboardStore(state => state.setDashboard);
  const [currentConfig, setCurrentConfig] = useState<IDashboardComponent>(
    component || defaultComponent(DashboardLayoutType.HORIZONTAL_FLEX)
  );
  const tabId = useTabId();

  const handleCancelAddingComponent = () => {
    setIsVisible(false);
  };

  const handleAddOrEditComponentInCurrentTab = () => {
    if (!dashboard) return;

    const tab = dashboard.dashboardTabsJs?.[tabId];
    if (!tab) return;

    const targetGroup = tab.componentGroups.find(g => g.id === groupId);
    if (!targetGroup) return;

    const existingComponents = targetGroup.headerConfig?.components ?? [];

    const isEdit = mode === ConfiguratorModes.EDIT;

    const updatedComponent: IDashboardComponent = isEdit ? currentConfig : { ...currentConfig, id: uuidv4() };

    const updatedComponents = isEdit
      ? existingComponents.map(c => (c.id === updatedComponent.id ? updatedComponent : c))
      : [...existingComponents, updatedComponent];

    const getComponentGroups = (currentTab: DashboardTabModel) => {
      return currentTab.componentGroups.map(g =>
        g.id !== groupId
          ? g
          : {
              ...g,
              headerConfig: {
                ...g.headerConfig,
                components: updatedComponents,
              },
            }
      );
    };

    const tabs = dashboard.dashboardTabsJs.map((t, i) =>
      i !== tabId
        ? t
        : {
            ...t,
            componentGroups: getComponentGroups(t),
          }
    );

    const newDashboard: Dashboard = {
      ...dashboard,
      dashboardTabsJs: tabs,
    };

    setDashboard(newDashboard);
    setIsVisible(false);
    setCurrentConfig(defaultComponent(DashboardLayoutType.HORIZONTAL_FLEX));
  };

  return (
    <Dialog
      visible={isVisible}
      onHide={() => setIsVisible(false)}
      header={mode === ConfiguratorModes.ADD ? T("action.add-component") : T("action.edit-component")}
      footer={
        <div>
          <Button
            label={T("word.cancel")}
            icon="pi pi-times"
            onClick={handleCancelAddingComponent}
            className="component-editor-button icon-red"
          />
          <Button
            label={T("action.save")}
            icon="pi pi-plus"
            onClick={handleAddOrEditComponentInCurrentTab}
            className="component-editor-button icon-green"
          />
        </div>
      }
      style={{ width: "70rem" }}
    >
      <div>
        <div className="p-fluid flex flex-row justify-content-between">
          <ComponentTypeEditor
            type={currentConfig?.type}
            currentConfig={currentConfig}
            setCurrentConfig={setCurrentConfig}
            isGroupHeaderComponent
          />
          <ComponentThemeEditor currentConfig={currentConfig} setCurrentConfig={setCurrentConfig} />
        </div>
        <ComponentEditor currentConfig={currentConfig} setCurrentConfig={setCurrentConfig} />
      </div>
    </Dialog>
  );
};

export default GroupHeaderComponentConfigurator;
